/**
 * Artwork: every painted piece the app has a place for, and the words that go
 * with it.
 *
 * The pictures themselves are made outside the app, with an image generator,
 * from docs/ART_BRIEF.md. This file is the other half of that brief: the name
 * each picture is saved under, and the description a screen reader gives of it.
 *
 * How a picture gets in:
 *   1. Save the generator's output in art-originals/ under the `file` name
 *      below (portrait-philemon.png, plate-1.png, scene-courtyard.png).
 *   2. npm run art      sizes it and writes the WebP copies to src/assets/art/
 *   3. Build. Nothing here needs editing.
 *
 * A place with no picture shows what it shows today: the guide's icon, the
 * evening's emblem, or nothing at all.
 *
 * The descriptions say what the brief asks for. If a finished picture differs
 * (the cloak is grey, the jug is in the other hand), change the description to
 * match the picture, not the other way round.
 *
 * Australian English throughout.
 */

export const PORTRAITS = {
  philemon: {
    file: 'portrait-philemon',
    name: 'Philemon',
    alt: 'Portrait of Philemon: a lean man of about fifty with kind eyes, a short greying beard and a small, warm smile, in an olive-green cloak.',
  },
  xenia: {
    file: 'portrait-xenia',
    name: 'Xenia',
    alt: 'Portrait of Xenia: a woman of about fifty with laughter lines and dark hair bound up, in a sea-blue mantle with a woven border.',
  },
  theron: {
    file: 'portrait-theron',
    name: 'Theron',
    alt: 'Portrait of Theron: a broad, weathered man of about sixty with a grey beard and a rust-red cloak, on the point of saying something.',
  },
  daria: {
    file: 'portrait-daria',
    name: 'Daria',
    alt: 'Portrait of Daria: a young woman with her dark hair in a plait, in a plain undyed dress, holding a wax tablet flat against her chest. There is ink on the back of her hand.',
  },
};

// One plate for each evening: the object or the moment that evening turns on.
// Nobody is shown from the front where a back or a pair of hands will do, and
// the reader is never shown at all. Every plate is seen from the reader's seat.
export const PLATES = {
  1: {
    file: 'plate-1',
    title: 'The first cup',
    alt: 'Two hands pour wine from a clay jug into a cup by lamplight. The lip of the jug rests against the rim, and wine has run over the fingers onto the stone.',
  },
  2: {
    file: 'plate-2',
    title: 'Another man\'s voice',
    alt: 'A moth circles the flame of a small clay lamp on a low table. Behind it, dim in the shadow, a man stands very upright with his chin raised, giving a lecture.',
  },
  3: {
    file: 'plate-3',
    title: 'Three grapes',
    alt: 'Three grapes set apart on a low wooden table beside a bowl of grapes. A hand with ink on its fingers rests next to them.',
  },
  4: {
    file: 'plate-4',
    title: 'Under the gate lamp',
    alt: 'A young woman stands under the lamp at a courtyard gate with her back to us and her head bent, writing on the back of her hand. A wax tablet is tucked under her arm.',
  },
  5: {
    file: 'plate-5',
    title: 'The far wall',
    alt: 'A man stands at the far wall of a quiet courtyard with his back to the viewer. The lamps are lit and the cushions round the low table are empty.',
  },
  6: {
    file: 'plate-6',
    title: 'Bare stone',
    alt: 'A young woman stands alone in the middle of an empty lamplit courtyard, her feet planted and a wax tablet hanging at her side. The low table stands on its end against the wall.',
  },
  7: {
    file: 'plate-7',
    title: 'Three words',
    alt: 'Two hands held out over a shipping crate, their backs covered to the wrist in small ink notes, with one patch rubbed clean. Three wax tablets and a garland of bay lie on the crate.',
  },
  8: {
    file: 'plate-8',
    title: 'The jug is yours',
    alt: 'Morning light in the courtyard after the gathering. A woman\'s hands hold a clay wine jug out towards the viewer, and a bay garland hangs over the gate behind her by one end.',
  },
};

export const SCENES = {
  courtyard: {
    file: 'scene-courtyard',
    title: 'Xenia\'s courtyard',
    alt: 'A small courtyard in ancient Athens at dusk, seen from the gate. Oil lamps burn on low stone walls and herbs grow between the paving stones. Three people sit round a low table, one of them looking this way, and a fourth cushion is empty.',
  },
};

// Shown wherever a painted piece is on the page. The vector ornament (the
// emblems and the key borders) was drawn for the app and needs no credit.
export const ART_CREDIT =
  'The paintings in this app are original pieces made for it with an AI image generator, in the manner of Renaissance painting. None is a copy of a historical work.';

/** Every file stem the app looks for, in the order the brief lists them. */
export const ART_FILES = [
  ...Object.values(PORTRAITS),
  ...Object.values(SCENES),
  ...Object.values(PLATES),
].map((piece) => piece.file);
